import { PercentPipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Pipe,
  PipeTransform,
  signal,
} from "@angular/core";
import { AccountMarketplace, AlgoMode } from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import { StrategyEx } from "@front/m19-models";
import { IDateRange, IIconComponent, ISelectMenuImports, ITooltipDirective, ToasterService } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";

interface PrimeDayConfig {
  primeDayPeriod: string;
  primeDayAfterPeriod: string;
  primeDayOverDate: string;
}

export const eventName = "Promo Days";
const configDateFormatting = "MMM d";

const BoostIntensityLabels: Record<number, string> = {
  [-50]: "-50%",
  [-25]: "-25%",
  [0]: "0%",
  [25]: "+25%",
  [50]: "+50%",
  [100]: "100%",
};

@Pipe({ name: "boostIntensityLabel", standalone: true })
export class BoostIntensityLabelPipe implements PipeTransform {
  transform(value: number | undefined | null): string {
    return value != null ? (BoostIntensityLabels[value] ?? "") : "";
  }
}

@Component({
  selector: "app-optimizer-description",
  templateUrl: "./optimizer-description.renderer.component.html",
  imports: [
    TranslocoDirective,
    ISelectMenuImports,
    IIconComponent,
    PercentPipe,
    ITooltipDirective,
    BoostIntensityLabelPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OptimizerDescriptionRendererComponent {
  private readonly strategyService = inject(StrategyService);
  private readonly toasterService = inject(ToasterService);

  strategy = input.required<StrategyEx>();
  accountMarketplace = input.required<AccountMarketplace>();
  isReadOnly = input.required<boolean>();
  dateRange = input.required<IDateRange>();
  AlgoMode = AlgoMode;

  protected readonly boostIntensities = [-50, -25, 0, 25, 50, 100] as const;
  protected readonly selectedBoost = computed(() => this.strategy().primeDayBoost);
  config = computed(() => primeDayConfig(this.dateRange()));
  isStrategyEligible = computed(
    () => this.strategy().algoMode === AlgoMode.ACOS_TARGET && !this.strategy().dailyBudget,
  );
  primeDayBoost = computed(
    () => this.strategy().acosTarget + this.strategy().acosTarget * (this.strategy().primeDayBoost! / 100),
  );
  primeDayDecrease = computed(() => this.strategy().acosTarget * 0.75);
  loading = signal(false);

  setIntensity(primeDayBoost: number | undefined): void {
    if (primeDayBoost == null) return;
    this.strategyService
      .updateStrategyPrimeDayBoost(
        this.accountMarketplace().accountId,
        this.accountMarketplace().marketplace,
        this.accountMarketplace().resourceOrganizationId!,
        this.strategy().strategyId,
        primeDayBoost,
      )
      .subscribe({
        next: () => {
          this.toasterService.success("Prime Day boost updated");
        },
        error: (error) => {
          this.toasterService.error(error.error.message);
        },
      });
  }
}

function primeDayConfig(dateRange: IDateRange): PrimeDayConfig {
  const afterDate = dateRange.end!.plus({ days: 1 });
  const afterDate2 = dateRange.end!.plus({ days: 2 });
  const overDate = dateRange.end!.plus({ days: 3 });

  return {
    primeDayPeriod:
      "from " + dateRange.start.toFormat(configDateFormatting) + " to " + dateRange.end!.toFormat(configDateFormatting),
    primeDayAfterPeriod:
      "from " + afterDate.toFormat(configDateFormatting) + " to " + afterDate2.toFormat(configDateFormatting),
    primeDayOverDate: overDate.toFormat(configDateFormatting),
  };
}
